// Drip Sequences v2 (PRD §4.4 / WP-09).
// Build multi-step approved template drips, delivery windows, re-entry policies, quiet-hours compliance.
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import Modal from '../components/ui/Modal';
import Toggle from '../components/ui/Toggle';
import Card from '../components/ui/Card';
import StatCard from '../components/ui/StatCard';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { useSubscribers } from '../lib/subscribers';
import { useTemplates } from '../lib/whatsappApi';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  useSequences,
  saveSequence,
  deleteSequence,
  enrollSubscribers,
  type Sequence,
  type SeqInput,
  type SequenceStep,
  type SequenceType,
  type ReEntryPolicy,
} from '../lib/sequences';
import {
  ClockIcon,
  CheckIcon,
  PlusIcon,
  PlayIcon,
  ExternalLinkIcon,
  SearchIcon,
  TrashIcon,
} from '../components/icons';

const COMMON_TIMEZONES = [
  'Asia/Muscat',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Africa/Cairo',
  'Asia/Amman',
  'Asia/Beirut',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'UTC',
];

function newStep(): SequenceStep {
  const id =
    globalThis.crypto?.randomUUID?.() ??
    `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return { id, delayHours: 24, templateName: '', templateLanguage: 'en' };
}

const emptySeq = (): SeqInput => {
  let userTz = 'UTC';
  try {
    userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Muscat';
  } catch {}

  return {
    name: '',
    enabled: true,
    type: 'time_based',
    reEntryPolicy: 'never',
    deliveryWindow: {
      preferredTime: '10:00',
      timezone: userTz,
    },
    steps: [{ ...newStep(), delayHours: 0 }],
  };
};

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function Sequences() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { sequences, loading } = useSequences();
  const { subscribers, labels } = useSubscribers();
  const { items: templates } = useTemplates();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | SequenceType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft'>('all');

  const [editing, setEditing] = useState<SeqInput | null>(null);
  const [useDeliveryWindow, setUseDeliveryWindow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [enroll, setEnroll] = useState<Sequence | null>(null);
  const [enrollLabel, setEnrollLabel] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // Quick stats
  const stats = useMemo(() => {
    const total = sequences.length;
    const active = sequences.filter((s) => s.enabled).length;
    const totalEnrolled = sequences.reduce((sum, s) => sum + (s.enrolled || 0), 0);
    const totalCompleted = sequences.reduce((sum, s) => sum + (s.completed || 0), 0);
    return { total, active, totalEnrolled, totalCompleted };
  }, [sequences]);

  // Filtered sequences
  const filteredSequences = useMemo(() => {
    return sequences.filter((s) => {
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== 'all' && s.type !== typeFilter) return false;
      if (statusFilter === 'active' && !s.enabled) return false;
      if (statusFilter === 'draft' && s.enabled) return false;
      return true;
    });
  }, [sequences, search, typeFilter, statusFilter]);

  const openCreateModal = () => {
    const fresh = emptySeq();
    setEditing(fresh);
    setUseDeliveryWindow(Boolean(fresh.deliveryWindow));
    setErr(null);
  };

  const openEditModal = (s: Sequence) => {
    setEditing({
      id: s.id,
      name: s.name,
      enabled: s.enabled,
      type: s.type,
      reEntryPolicy: s.reEntryPolicy,
      deliveryWindow: s.deliveryWindow || null,
      steps: s.steps.length ? s.steps : [newStep()],
    });
    setUseDeliveryWindow(Boolean(s.deliveryWindow));
    setErr(null);
  };

  const patchStep = (i: number, patch: Partial<SequenceStep>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      steps: editing.steps.map((s, x) => (x === i ? { ...s, ...patch } : s)),
    });
  };

  const handleSave = async () => {
    if (!editing || !editing.name.trim()) return;
    const validSteps = editing.steps.filter((s) => s.templateName.trim());
    if (!validSteps.length) {
      setErr(t('seq.needStep'));
      return;
    }

    setBusy(true);
    setErr(null);

    const payload: SeqInput = {
      ...editing,
      deliveryWindow: useDeliveryWindow && editing.deliveryWindow ? editing.deliveryWindow : null,
      steps: validSteps,
    };

    try {
      await saveSequence(payload);
      toast.success(editing.id ? t('seq.updatedSuccess') : t('seq.createdSuccess'));
      setEditing(null);
    } catch (e: any) {
      setErr(e.message || t('seq.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('seq.deleteConfirm', { name }), danger: true }))) return;
    try {
      await deleteSequence(id);
      toast.success(t('seq.deletedSuccess'));
    } catch (e: any) {
      toast.error(e.message || t('seq.deleteFailed'));
    }
  };

  const handleEnrollConfirm = async () => {
    if (!enroll) return;
    const ids = subscribers
      .filter((s) => (enrollLabel ? s.labels.includes(enrollLabel) : true))
      .map((s) => s.id);

    if (!ids.length) {
      toast.warning(t('seq.noSubs'));
      return;
    }

    setEnrolling(true);
    try {
      const res = await enrollSubscribers(enroll.id, ids);
      toast.success(
        t('seq.enrollResult', {
          enrolled: res.enrolled,
          skipped: res.skipped || 0,
        })
      );
      setEnroll(null);
    } catch (e: any) {
      toast.error(e.message || t('seq.enrollFailed'));
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-ink-3 dark:text-slate-400">
            <button
              type="button"
              onClick={() => navigate('/bots')}
              className="hover:text-ink dark:hover:text-slate-200 transition"
            >
              {t('bot.title')}
            </button>
            <span>/</span>
            <span className="text-ink dark:text-slate-200">{t('seq.title')}</span>
          </div>
          <h1 className="text-xl font-extrabold text-ink dark:text-slate-100 mt-1">
            {t('seq.title')}
          </h1>
          <p className="text-xs text-ink-3 dark:text-slate-400">
            {t('seq.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/flows')}
            className="text-xs"
          >
            <ExternalLinkIcon size={14} />
            <span>{t('seq.buildAsFlow')}</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={openCreateModal}
            className="text-xs"
          >
            <PlusIcon size={14} />
            <span>{t('seq.add')}</span>
          </Button>
        </div>
      </div>

      {/* Visual Flow Builder Callout Banner */}
      <div className="p-3.5 rounded-2xl border border-brand/20 bg-brand/5 dark:bg-brand-950/20 dark:border-brand-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="text-xl shrink-0">🔀</span>
          <div>
            <h4 className="text-xs font-bold text-ink dark:text-slate-200">
              {t('seq.flowBannerTitle')}
            </h4>
            <p className="text-[11px] text-ink-3 dark:text-slate-400 mt-0.5">
              {t('seq.flowBannerDesc')}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/flows')}
          className="text-xs text-brand font-bold shrink-0 self-start sm:self-auto"
        >
          <span>{t('seq.openFlowBuilder')}</span> →
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={t('seq.statsTotal')}
          value={stats.total}
          icon={<ClockIcon size={18} className="text-blue-500" />}
        />
        <StatCard
          label={t('seq.statsActive')}
          value={stats.active}
          icon={<CheckIcon size={18} className="text-emerald-500" />}
        />
        <StatCard
          label={t('seq.statsEnrolled')}
          value={stats.totalEnrolled}
          icon={<PlayIcon size={18} className="text-amber-500" />}
        />
        <StatCard
          label={t('seq.statsCompleted')}
          value={stats.totalCompleted}
          icon={<CheckIcon size={18} className="text-brand" />}
        />
      </div>

      {/* Filters Bar */}
      <Card className="p-3.5 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex-1 relative min-w-[200px]">
          <SearchIcon
            size={14}
            className="absolute start-3 top-1/2 -translate-y-1/2 text-ink-4 dark:text-slate-500"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('seq.searchPlaceholder')}
            className="w-full rounded-xl border border-line bg-surface ps-9 pe-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800/80 dark:text-slate-100"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="rounded-xl border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">{t('seq.allTypes')}</option>
            <option value="time_based">{t('seq.typeTimeBased')}</option>
            <option value="action_based">{t('seq.typeActionBased')}</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-xl border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">{t('seq.allStatuses')}</option>
            <option value="active">{t('seq.statusActive')}</option>
            <option value="draft">{t('seq.statusDraft')}</option>
          </select>
        </div>
      </Card>

      {/* Sequences List */}
      {loading ? (
        <div className="py-12 text-center text-xs text-ink-3 dark:text-slate-400">
          <span className="animate-spin inline-block me-2">⏳</span>
          {t('common.loading')}
        </div>
      ) : filteredSequences.length === 0 ? (
        <EmptyState
          title={t('seq.empty')}
          description={t('seq.emptyDesc')}
          action={
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              <PlusIcon size={14} />
              <span>{t('seq.add')}</span>
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSequences.map((s) => (
            <Card
              key={s.id}
              className="p-4 flex flex-col justify-between hover:shadow-md transition"
            >
              <div>
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2.5">
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-bold text-ink dark:text-slate-100 truncate"
                      title={s.name}
                    >
                      {s.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {s.enabled ? (
                        <Badge variant="success" size="sm">
                          {t('forms.live')}
                        </Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">
                          {t('forms.off')}
                        </Badge>
                      )}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 font-medium">
                        {s.type === 'action_based'
                          ? t('seq.badgeAction')
                          : t('seq.badgeTime')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Attributes */}
                <div className="space-y-1.5 py-2.5 border-y border-line dark:border-slate-800 text-xs text-ink-2 dark:text-slate-300">
                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('seq.deliveryWindowLabel')}:
                    </span>
                    <span className="font-mono text-[11px] truncate max-w-[170px]">
                      {s.deliveryWindow
                        ? `${s.deliveryWindow.preferredTime} (${s.deliveryWindow.timezone})`
                        : t('seq.windowAnytime')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('seq.reEntryPolicyLabel')}:
                    </span>
                    <span className="text-[11px]">
                      {s.reEntryPolicy === 'after_completion'
                        ? t('seq.reEntryAfter')
                        : s.reEntryPolicy === 'always'
                        ? t('seq.reEntryAlways')
                        : t('seq.reEntryNever')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('seq.stepsCount')}:
                    </span>
                    <span className="font-semibold">{s.steps.length}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('seq.progress')}:
                    </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {s.enrolled} {t('seq.enrolledShort')} · {s.completed}{' '}
                      {t('seq.completedShort')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-[11px] text-ink-3 dark:text-slate-400">
                    <span>{t('seq.lastSent')}:</span>
                    <span>{fmtDate(s.lastSentAt)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 flex items-center justify-between gap-1.5 pt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEnrollLabel('');
                    setEnroll(s);
                  }}
                  className="text-xs flex-1"
                >
                  <PlayIcon size={12} />
                  <span>{t('seq.enrollBtn')}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditModal(s)}
                  className="text-xs"
                >
                  {t('cat.edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(s.id, s.name)}
                  className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                >
                  <TrashIcon size={13} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('seq.editTitle') : t('seq.add')}
      >
        {editing && (
          <div className="space-y-4 max-h-[75vh] overflow-y-auto pe-1">
            {/* Name */}
            <div>
              <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                {t('seq.name')} <span className="text-rose-500">*</span>
              </label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder={t('seq.namePlaceholder')}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                autoFocus
              />
            </div>

            {/* Status toggle */}
            <div className="flex items-center justify-between rounded-xl border border-line bg-surface p-3 dark:border-slate-800 dark:bg-slate-900">
              <div>
                <span className="block text-xs font-bold text-ink dark:text-slate-200">
                  {t('forms.enabled')}
                </span>
                <span className="text-[11px] text-ink-3 dark:text-slate-400">
                  {t('seq.enabledHelp')}
                </span>
              </div>
              <Toggle
                on={editing.enabled}
                onChange={(v) => setEditing({ ...editing, enabled: v })}
                label={t('forms.enabled')}
              />
            </div>

            {/* Type & Re-entry Policy */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                  {t('seq.typeLabel')}
                </label>
                <select
                  value={editing.type}
                  onChange={(e) =>
                    setEditing({ ...editing, type: e.target.value as SequenceType })
                  }
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="time_based">{t('seq.typeTimeBased')}</option>
                  <option value="action_based">{t('seq.typeActionBased')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                  {t('seq.reEntryPolicyLabel')}
                </label>
                <select
                  value={editing.reEntryPolicy}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      reEntryPolicy: e.target.value as ReEntryPolicy,
                    })
                  }
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="never">{t('seq.reEntryNever')}</option>
                  <option value="after_completion">{t('seq.reEntryAfter')}</option>
                  <option value="always">{t('seq.reEntryAlways')}</option>
                </select>
              </div>
            </div>

            {/* Delivery Window Section */}
            <div className="rounded-xl border border-line bg-surface p-3.5 space-y-3 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-xs font-bold text-ink dark:text-slate-200">
                    {t('seq.deliveryWindowToggle')}
                  </span>
                  <span className="text-[11px] text-ink-3 dark:text-slate-400">
                    {t('seq.deliveryWindowHelp')}
                  </span>
                </div>
                <Toggle
                  on={useDeliveryWindow}
                  onChange={(v) => {
                    setUseDeliveryWindow(v);
                    if (v && !editing.deliveryWindow) {
                      setEditing({
                        ...editing,
                        deliveryWindow: {
                          preferredTime: '10:00',
                          timezone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone ||
                            'Asia/Muscat',
                        },
                      });
                    }
                  }}
                  label={t('seq.deliveryWindowToggle')}
                />
              </div>

              {useDeliveryWindow && editing.deliveryWindow && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-line dark:border-slate-800">
                  <div>
                    <label className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400 mb-1">
                      {t('seq.preferredTime')} (HH:mm)
                    </label>
                    <input
                      type="time"
                      value={editing.deliveryWindow.preferredTime}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          deliveryWindow: {
                            ...editing.deliveryWindow!,
                            preferredTime: e.target.value,
                          },
                        })
                      }
                      className="w-full rounded-xl border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400 mb-1">
                      {t('seq.timezone')}
                    </label>
                    <select
                      value={editing.deliveryWindow.timezone}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          deliveryWindow: {
                            ...editing.deliveryWindow!,
                            timezone: e.target.value,
                          },
                        })
                      }
                      className="w-full rounded-xl border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                    >
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Quiet Hours Compliance Note */}
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
              <span className="font-bold">{t('seq.quietHoursTitle')}:</span>{' '}
              {t('seq.quietHoursNotice')}
            </div>

            {/* Steps Builder */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-ink dark:text-slate-200">
                    {t('seq.steps')} ({editing.steps.length})
                  </span>
                  <p className="text-[11px] text-ink-3 dark:text-slate-400">
                    {t('seq.templateNote')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setEditing({ ...editing, steps: [...editing.steps, newStep()] })
                  }
                  disabled={editing.steps.length >= 20}
                  className="text-xs"
                >
                  <PlusIcon size={12} />
                  <span>{t('seq.addStep')}</span>
                </Button>
              </div>

              <div className="space-y-2.5">
                {editing.steps.map((st, i) => (
                  <div
                    key={st.id}
                    className="rounded-xl border border-line bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="mb-2 flex items-center justify-between text-xs font-semibold text-ink-3 dark:text-slate-400">
                      <span className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-brand/10 text-brand text-[11px] font-bold grid place-items-center">
                          {i + 1}
                        </span>
                        <span>{t('seq.step')} {i + 1}</span>
                      </span>
                      {editing.steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              steps: editing.steps.filter((_, x) => x !== i),
                            })
                          }
                          className="text-rose-500 hover:text-rose-700 text-xs font-bold px-1.5 py-0.5"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="block text-[11px] text-ink-3 dark:text-slate-400 mb-0.5">
                          {t('seq.delayHours')}
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={8760}
                          value={st.delayHours}
                          onChange={(e) =>
                            patchStep(i, { delayHours: Math.max(0, Number(e.target.value)) })
                          }
                          className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-ink-3 dark:text-slate-400 mb-0.5">
                          {t('seq.lang')}
                        </label>
                        <input
                          value={st.templateLanguage}
                          onChange={(e) =>
                            patchStep(i, { templateLanguage: e.target.value.toLowerCase() })
                          }
                          placeholder="en"
                          className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-ink-3 dark:text-slate-400 mb-0.5">
                          {t('seq.template')}
                        </label>
                        {templates.length > 0 ? (
                          <select
                            value={st.templateName}
                            onChange={(e) =>
                              patchStep(i, { templateName: e.target.value })
                            }
                            className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                          >
                            <option value="">— {t('seq.selectTemplate')} —</option>
                            {templates
                              .filter((tpl) => tpl.status === 'APPROVED')
                              .map((tpl) => (
                                <option key={tpl.id || tpl.name} value={tpl.name}>
                                  {tpl.name} ({tpl.language})
                                </option>
                              ))}
                          </select>
                        ) : (
                          <input
                            value={st.templateName}
                            onChange={(e) =>
                              patchStep(i, { templateName: e.target.value })
                            }
                            placeholder="approved_template_name"
                            className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {err && (
              <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
                {err}
              </div>
            )}

            {/* Footer buttons */}
            <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-800">
              <Button
                variant="ghost"
                onClick={() => setEditing(null)}
                disabled={busy}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={busy || !editing.name.trim()}
              >
                {busy ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Enroll Modal */}
      <Modal
        open={enroll !== null}
        onClose={() => setEnroll(null)}
        title={t('seq.enrollTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-2 dark:text-slate-300">
            {t('seq.enrollDesc')}
          </p>

          <div>
            <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
              {t('seq.targetLabel')}
            </label>
            <select
              value={enrollLabel}
              onChange={(e) => setEnrollLabel(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="">{t('seq.allSubs')}</option>
              {labels.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          <div className="p-3 rounded-xl bg-surface border border-line dark:border-slate-800 dark:bg-slate-900 text-xs text-ink-3 dark:text-slate-400">
            <span>{t('seq.matchingCount')}: </span>
            <span className="font-bold text-ink dark:text-slate-100">
              {
                subscribers.filter((s) =>
                  enrollLabel ? s.labels.includes(enrollLabel) : true
                ).length
              }
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-line dark:border-slate-800">
            <Button
              variant="ghost"
              onClick={() => setEnroll(null)}
              disabled={enrolling}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleEnrollConfirm}
              disabled={enrolling}
            >
              {enrolling ? t('common.enrolling') : t('seq.enrollBtn')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
